import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { getActiveTicket, getIsTicketUpdate, getSidebarView } from 'store/ticket-service/selector';
import {
	setActiveHistoryItem,
	setHistoryMode,
	setIsTariffPlansModalOpen,
	setPaymentMode,
	setReportAnIssueMode,
	setSidebarView,
	setUpdatePaymentInfoModal,
} from 'store/ticket-service/ticketSlice';
import { setIsCreateTicketShareOpen } from 'store/ticket-share-service/ticketShareSlice';
import { getActiveSubscription, getAllPaymentMethods } from 'store/user-service/asyncActions';
import {
	getCurrentSubscription,
	getIsOpenUserProfile,
	getPaymentMethods,
	getUserBalance,
} from 'store/user-service/selector';
import { setCurrentSubscription, setIsOpenUserProfile } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';
import { getLocalStorageBalance } from 'utils/localStorageHelpers/balanceHelpers';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { useGetUser } from 'core/hooks/useGetUser';
import { PaymentMethodType } from 'core/types/base';

import { CreateTicketModal } from 'components/Ticket/CreateTicketModal';
import { CustomActionTooltip } from 'components/shared/CustomActionTooltip';
import { CustomTooltipElement } from 'components/shared/CustomDisabledElement/CustomTooltipElement';
import { LanguageSelect } from 'components/shared/CustomSelect/LanguageSelect';

import { ReactComponent as TokensIcon } from 'assets/icons/coins.svg';
import { ReactComponent as Logo } from 'assets/icons/logo-ellogy.svg';
import { ReactComponent as Logout } from 'assets/icons/logout.svg';
import { ReactComponent as MessageQuestion } from 'assets/icons/message-question.svg';
import { ReactComponent as NotificationsTab } from 'assets/icons/notifications-tab.svg';
import { ReactComponent as ProjectsTab } from 'assets/icons/projects-tab.svg';
import { ReactComponent as RequestsTab } from 'assets/icons/requests-tab.svg';
import { ReactComponent as SettingsTab } from 'assets/icons/settings-tab.svg';
import { ReactComponent as SupportTab } from 'assets/icons/support-tab.svg';

import { CustomAvatar } from '../CustomAvatar/CustomAvatar';
import { LogoutModal } from '../LogoutModal/LogoutModal';

import * as Styled from './styled';

interface HeaderProps {
	isCreating?: boolean;
}

interface ISidebarItem {
	titlePath: string;
	icon: ReactElement;
	selected: boolean;
	isInDevelopment: boolean;
	onClick: () => any;
}

export const Header: FC<HeaderProps> = ({ isCreating }) => {
	const { i18n } = useTranslation();
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const isPaymentEnabled = process.env?.REACT_APP_ENABLE_PAYMENTS
		? process.env.REACT_APP_ENABLE_PAYMENTS.toLowerCase() === 'true'
		: true;

	const activeTicket = useSelector(getActiveTicket);
	const isTicketUpdate = useSelector(getIsTicketUpdate);
	const sidebarView = useSelector(getSidebarView);
	const userBalance = useSelector(getUserBalance);
	const currentSubscription = useSelector(getCurrentSubscription);
	const isUserProfileOpen = useSelector(getIsOpenUserProfile);
	const paymentMethods = useSelector(getPaymentMethods);

	const handleCloseUserProfile = () => {
		if (isUserProfileOpen) dispatch(setIsOpenUserProfile(false));
	};

	const handleReportAnIssue = () => {
		if (activeTicket) dispatch(setReportAnIssueMode(true));

		handleCloseUserProfile();
	};

	const handleOpenProfile = () => {
		if (isTicketUpdate) {
			setIsShowDraft && setIsShowDraft(true);
		} else {
			handleProfileAction();
		}
	};

	const handleOpenRequests = () => {
		dispatch(setReportAnIssueMode(false));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(setIsOpenUserProfile(false));
		dispatch(setIsCreateTicketShareOpen(false));
	};

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
	const [isContentOverflowing, setIsContentOverflowing] = useState(false);
	const [isShowDraft, setIsShowDraft] = useState(false);
	const [cachedBalance, setCachedBalance] = useState(0);
	const [sidebarTabsConfig, setSidebarTabsConfig] = useState<Array<ISidebarItem>>([
		{
			titlePath: 'sidebarTabs.projects',
			icon: <ProjectsTab />,
			selected: false,
			isInDevelopment: true,
			onClick: () => null,
		},

		{
			titlePath: 'sidebarTabs.requests',
			icon: <RequestsTab />,
			selected: true,
			isInDevelopment: false,
			onClick: handleOpenRequests,
		},
		{
			titlePath: 'sidebarTabs.notifications',
			icon: <NotificationsTab />,
			selected: false,
			isInDevelopment: true,
			onClick: () => null,
		},
		{
			titlePath: 'sidebarTabs.settings',
			icon: <SettingsTab />,
			selected: false,
			isInDevelopment: false,
			onClick: handleOpenProfile,
		},
		{
			titlePath: 'sidebarTabs.support',
			icon: <SupportTab />,
			selected: false,
			isInDevelopment: false,
			onClick: handleReportAnIssue,
		},
	]);

	const { user } = useGetUser();

	useEffect(() => {
		setCachedBalance(getLocalStorageBalance());
	}, []);

	const userBillingCheck = useMemo(() => {
		return (
			user?.stripeCustomerId === null ||
			user?.stripeCustomerId === '' ||
			paymentMethods?.length === 0 ||
			!paymentMethods?.find((item: PaymentMethodType) => item.default)
		);
	}, [user?.stripeCustomerId, paymentMethods]);

	const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

	const handleClickOnMenu = (event: React.MouseEvent<HTMLDivElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setTimeout(() => {
			setAnchorEl(null);
		}, 1);
	};

	const handleSidebarTabClick = (selectedTab: ISidebarItem) => {
		const updatedTabs = sidebarTabsConfig.map((tab) => ({
			...tab,
			selected: tab.titlePath === selectedTab.titlePath,
		}));

		setSidebarTabsConfig(updatedTabs);

		switch (selectedTab.titlePath) {
			case 'sidebarTabs.support':
				handleReportAnIssue();
				break;
			case 'sidebarTabs.settings':
				handleOpenProfile();
				break;
			case 'sidebarTabs.requests':
				handleOpenRequests();
				break;
			default:
				return;
		}
	};

	const handleGetAllPaymentsMethods = async () => {
		try {
			await dispatch(getAllPaymentMethods({ recordsPerPage: 5 })).unwrap();
		} catch (error) {
			console.error('getAllPaymentMethods error', error);
		}
	};

	const handleGetCurrentSubscriptionLevel = async () => {
		if (user) {
			const activeTariff = await dispatch(getActiveSubscription()).unwrap();

			if (activeTariff) dispatch(setCurrentSubscription(activeTariff));
		}
	};

	const locationObject = useLocation();

	const logout = () => {
		if (isTicketUpdate) {
			setIsShowDraft && setIsShowDraft(true);
		}
		!isShowDraft && setIsLogoutModalOpen(true);
		setAnchorEl(null);
	};

	const handleProfileAction = () => {
		dispatch(setReportAnIssueMode(false));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(setIsOpenUserProfile(true));
		handleClose();
	};

	const handleCloseCreateTicketModal = () => {
		if (setIsShowDraft) {
			handleProfileAction();
			setIsShowDraft(false);
		}
	};

	const handleCloseLogoutModal = () => {
		setIsLogoutModalOpen(false);
	};

	const firstNameRef = useRef<HTMLDivElement>(null);
	const lastNameRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const contentWidth =
			(firstNameRef.current?.offsetWidth || 0) + (lastNameRef.current?.offsetWidth || 0) > 128;
		setIsContentOverflowing(contentWidth);
	}, []);

	useEffect(() => {
		if (localStorage.getItem('i18nextLng') !== ('en' && 'ar')) {
			window.localStorage.setItem('i18nextLng', 'en');
		}
		if (i18n.language !== 'en' && i18n.language !== 'ar') {
			i18n.changeLanguage('en');
		}
	}, []);

	useEffect(() => {
		if (user && user?.stripeCustomerId && user?.stripeCustomerId.length > 0) {
			handleGetAllPaymentsMethods();
		}
	}, [user]);

	useEffect(() => {
		if (!currentSubscription) handleGetCurrentSubscriptionLevel();
	}, [currentSubscription]);

	const isLoginPage = useMemo(
		() =>
			locationObject.pathname === ROUTES.LOGIN ||
			locationObject.pathname === ROUTES.SIGNUP ||
			locationObject.pathname === ROUTES.RESET_PASSWORD ||
			locationObject.pathname === ROUTES.CREATE_NEW_PASSWORD,
		[locationObject.pathname]
	);

	const handlePaymentClick = () => {
		if (!userBillingCheck) {
			const localStorage = window.localStorage;

			if (localStorage?.paymentModalOpenings === undefined)
				localStorage.setItem('paymentModalOpenings', JSON.stringify({}));

			const paymentModalOpenings = JSON.parse(localStorage.paymentModalOpenings);

			if (paymentModalOpenings?.[user.id] === undefined) {
				localStorage.setItem(
					'paymentModalOpenings',
					JSON.stringify({ ...paymentModalOpenings, [user.id]: 1 })
				);
				dispatch(setPaymentMode(true));
			} else {
				if (paymentModalOpenings?.[user.id] < 2) {
					localStorage.setItem(
						'paymentModalOpenings',
						JSON.stringify({
							...paymentModalOpenings,
							[user.id]: paymentModalOpenings?.[user.id] + 1,
						})
					);

					dispatch(setPaymentMode(true));
				} else {
					dispatch(setIsTariffPlansModalOpen(true));
				}
			}
		} else {
			dispatch(
				setUpdatePaymentInfoModal({
					isOpen: true,
					paymentMethods: paymentMethods,
					previousUrlQuery: isUserProfileOpen ? 'isProfile=true' : '',
				})
			);
		}
	};

	const renderButton = () => {
		if (locationObject.pathname === ROUTES.LOGIN) {
			return (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '20px',
					}}
				>
					<LanguageSelect />
					<Button
						component={Link}
						to="/sign-up"
						sx={{
							width: '150px',
							height: '45px',
							border: '1px solid #4786FF',
							borderRadius: '8px',
							textTransform: 'none',
							fontWeight: '700',
						}}
					>
						{commonTranslate('buttons.signUp')}
					</Button>
				</Box>
			);
		} else if (
			locationObject.pathname === ROUTES.SIGNUP ||
			locationObject.pathname === ROUTES.RESET_PASSWORD ||
			locationObject.pathname === ROUTES.CREATE_NEW_PASSWORD
		) {
			return (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '20px',
					}}
				>
					<LanguageSelect />
					<Button
						component={Link}
						to="/sign-in"
						sx={{
							width: '150px',
							height: '45px',
							border: '1px solid #4786FF',
							borderRadius: '8px',
							textTransform: 'none',
							fontWeight: '700',
						}}
					>
						{commonTranslate('buttons.logIn')}
					</Button>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '20px',
					marginTop: '32px',
				}}
			>
				<Styled.SidebarItemsList>
					{sidebarTabsConfig.map((sidebarItem, index) =>
						sidebarItem.isInDevelopment ? (
							<CustomTooltipElement
								key={`${sidebarItem.titlePath}.${index}`}
								message={commonTranslate('text.inDevelopment')}
							>
								<Styled.SidebarItemContainer
									key={sidebarItem.titlePath}
									selected={sidebarItem.selected}
									disabled={true}
									onClick={() => handleSidebarTabClick(sidebarItem)}
								>
									{sidebarItem.icon}
									<Styled.SidebarItemText>
										{commonTranslate(sidebarItem.titlePath)}
									</Styled.SidebarItemText>
								</Styled.SidebarItemContainer>
							</CustomTooltipElement>
						) : (
							<Styled.SidebarItemContainer
								key={sidebarItem.titlePath}
								selected={sidebarItem.selected}
								onClick={() => handleSidebarTabClick(sidebarItem)}
							>
								{sidebarItem.icon}
								<Styled.SidebarItemText>
									{commonTranslate(sidebarItem.titlePath)}
								</Styled.SidebarItemText>
							</Styled.SidebarItemContainer>
						)
					)}
				</Styled.SidebarItemsList>
			</Box>
		);
	};

	return (
		<Box
			sx={{
				boxSizing: 'border-box',
				display: { xs: window.innerWidth < 900 && !sidebarView ? 'none' : 'flex', md: 'flex' },
				flexDirection: 'column',
				justifyContent: 'center',
				width: 'clamp(80px, 8%, 90px)',
				height: '100%',
				padding: '12px 0',
				borderBottom: '1px solid #E7E8EC',
				background: '#4786FF',
				boxShadow: '0px 4px 16px rgba(40, 103, 131, 0.06)',
				borderRadius: '8px 0 0 8px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					alignItems: 'center',
					height: '100%',
				}}
			>
				<Box
					sx={{
						display: {
							xs:
								locationObject.pathname === ROUTES.LOGIN ||
								locationObject.pathname === ROUTES.SIGNUP ||
								locationObject.pathname === ROUTES.RESET_PASSWORD ||
								locationObject.pathname === ROUTES.CREATE_NEW_PASSWORD
									? 'flex'
									: 'none',
							md: 'flex',
						},
						flexDirection: 'column',
						alignItems: 'center',
						gap: '24px',
						marginBottom: '24px',
					}}
				>
					<Logo />

					{isPaymentEnabled && !isLoginPage ? (
						<Styled.PaymentContainer>
							<Styled.PaymentButtonContainer className="rtl-able">
								<Styled.PaymentText>{userBalance || cachedBalance}</Styled.PaymentText>
								<CustomActionTooltip
									tooltipMessage={commonTranslate('tooltips.addDefaultPaymentMethodTooltip')}
									check={userBillingCheck}
								>
									<TokensIcon style={{ height: '16px' }} />
								</CustomActionTooltip>
							</Styled.PaymentButtonContainer>
							<Styled.BuyButton variant="outlined" color="secondary" onClick={handlePaymentClick}>
								<Styled.PaymentText>{commonTranslate('buttons.purchase')}</Styled.PaymentText>
							</Styled.BuyButton>
						</Styled.PaymentContainer>
					) : null}

					{renderButton()}
				</Box>

				<Button
					sx={{
						display: {
							xs: isLoginPage ? 'flex' : 'none',
							md: 'none',
						},
						height: '44px',
						padding: '0',
						minWidth: '44px',
						width: '44px',
						borderRadius: '8px',
						textTransform: 'none',
						fontSize: '16px',
						fontWeight: '700',
						gap: '8px',
					}}
					variant="outlined"
					color="primary"
					onClick={() => {
						activeTicket && dispatch(setReportAnIssueMode(true));
						activeTicket && dispatch(setSidebarView(false));
					}}
				>
					<MessageQuestion />
				</Button>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
					<LanguageSelect />

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '8px',
							maxWidth: '222px',
							width: isContentOverflowing ? 'min-content' : 'auto',
							cursor: 'pointer',
						}}
						onClick={(event) => handleClickOnMenu(event)}
					>
						<CustomAvatar />

						<Styled.ProfileInfoText>{user?.firstName}</Styled.ProfileInfoText>

						<Menu
							className="rtl-able"
							data-testid="anchor-element"
							PaperProps={{
								style: {
									width: 212,
									marginTop: '12px',
								},
							}}
							anchorEl={anchorEl}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							open={open}
							onClose={handleClose}
						>
							<MenuItem onClick={logout}>
								<Box
									sx={{
										...flexMixin({ gap: 12 }),
									}}
								>
									<Logout />
									<Typography sx={{ color: '#FB0B24' }}>
										{commonTranslate('buttons.logOut')}
									</Typography>
								</Box>
							</MenuItem>
						</Menu>
					</Box>
				</Box>
			</Box>
			{isShowDraft && activeTicket && (
				<CreateTicketModal
					handleCloseModal={handleCloseCreateTicketModal}
					ticket={activeTicket}
					userId={user.id}
					isCreating={isCreating}
				/>
			)}
			{isLogoutModalOpen && !isShowDraft && (
				<LogoutModal handleCloseModal={handleCloseLogoutModal} />
			)}
		</Box>
	);
};
